import { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationProvider }  from "react-bootstrap-table2-paginator";
import { getAuthorities } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/books/BooksAction";
import * as uiHelpers from "../BooksUIHelpers";
import { 
  getSelectRow, 
  getHandlerTableChange,  
  NoRecordsFoundMessage, 
  PleaseWaitMessage, 
  sortCaret
} from "../../../../../../_metronic/_helpers";
import * as columnFormatters from "./column-formatters";
import { Pagination } from "../../../../../../_metronic/_partials/controls";
import { useBooksUIContext } from "../BooksUIContext";

export function BooksTable({ 
  deleteBooks
}) {
  // Books UI Context
  const booksUIContext = useBooksUIContext();
  const booksUIProps = useMemo(() => {
    return {
      ids: booksUIContext.ids,
      setIds: booksUIContext.setIds,
      queryParams: booksUIContext.queryParams,
      setQueryParams: booksUIContext.setQueryParams,
      openEditBookPage: booksUIContext.openEditBookPage
    };
  }, [booksUIContext]);

  // Getting curret state of Books list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.books }),
    shallowEqual
  );
  const { totalCount, entities, authorities, listLoading } = currentState;
  
  // Authority Parameters
  const { isEditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // Books Redux state
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(actions.getBooks(booksUIProps.queryParams));
    return () => dispatch(actions.cleanBooksState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booksUIProps.queryParams, dispatch]);

  // Table columns
  const columns = [
    {
      dataField: "name",
      text: "Adı",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "200px",
      }
    },
    {
      dataField: "author",
      text: "Yazarı",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "180px",
      }
    },
    {
      dataField: "publisher",
      text: "Yayınevi",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "180px",
      }
    },
    {
      dataField: "categoryName",
      text: "Kategorisi",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "180px",
      }
    },
    {
      dataField: "typeName",
      text: "Türü",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "180px",
      }
    },
    {
      dataField: "grade",
      text: "Sınıf",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "120px",
      }
    },
    {
      dataField: "pageNumber",
      text: "Sayfa Sayısı",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "150px",
      }
    },
    {
      dataField: "createdBy",
      text: "Ekleyen",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "180px",
      }
    }
  ];

  const actionColumn = {
    dataField: "action",
    text: "İşlemler",
    formatter: columnFormatters.ActionsColumnFormatter,
    formatExtraData: {
      isEditionAuthorized,
      isDeletionAuthorized,
      openEditBookPage: booksUIProps.openEditBookPage,
      deleteBooks
    },
    classes: "text-right pr-0",
    headerClasses: "text-right pr-3",
    style: {
      minWidth: "100px",
    }
  }

  if (isEditionAuthorized || isDeletionAuthorized) columns.push(actionColumn);

  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: booksUIProps.queryParams.pageSize,
    page: booksUIProps.queryParams.pageNumber
  };
  
  return (
    <PaginationProvider pagination={paginationFactory(paginationOptions)}>
      {({ paginationProps, paginationTableProps }) => {
        return (
          <Pagination
            isLoading={listLoading}
            paginationProps={paginationProps}
          >
            <BootstrapTable
              wrapperClasses="table-responsive"
              classes="table table-head-custom table-vertical-center overflow-hidden"
              bootstrap4
              bordered={false}
              remote
              keyField="id"
              data={entities ?? []}
              columns={columns}
              defaultSorted={uiHelpers.defaultSorted}
              onTableChange={getHandlerTableChange(
                booksUIProps.setQueryParams
              )}
              selectRow={getSelectRow({
                entities,
                ids: booksUIProps.ids,
                setIds: booksUIProps.setIds,
                hidden: !isDeletionAuthorized
              })}
              {...paginationTableProps}
            >
              <PleaseWaitMessage entities={entities} />
              <NoRecordsFoundMessage entities={entities} />
            </BootstrapTable>
          </Pagination>
        );    
      }}
    </PaginationProvider>  
  );
}